import React, { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
    onSetSelectedShift,
    setAsOnlySelectedPassenger,
    setEditShift,
    setIsShiftDeleted
} from 'src/store/actions/PassengersShiftActionType';

import TrashIcon from 'src/screens/PassengerManger/components/Passengers/PassengersGrid/TrashIcon';
import MyEditPencilIcon from 'src/components/Icons/MyEditPencil';
import { Slide } from '@mui/material';
import { IRootReducer } from 'src/store/reducers';
import usePlacement from 'src/screens/ManualOrder/hook/usePlacement';
import styled from 'styled-components';
import _ from 'lodash';
import { useAppDispatch, useRootAppSelector } from 'src/store/hooks';
import { shiftHooks } from 'src/screens/ManualOrder/hook/hooks.Shift';
import {
    apiCallStatusesSelector,
    selectedPassengersIdsSelector,
    setCapsulesForDeletionByAllSelectedPassengersAction,
    setSelectedPassengersAction,
    setShiftTargetedToDeleteAction,
    shiftCapsulesByPassengerSelector,
} from 'src/store/slices/manualOrders/manualOrdersSlice';
import { manualOrdersCommonHooks } from 'src/screens/ManualOrder/hook/manualOrdersHooks.common';
import { stringifyDate } from 'src/utilis/utilis';
import * as Styles from '../Shiftcell.Style';
import DeleteModal from './DeleteModal';
import EditScopeModal from './EditScopeModal';
import { ShiftActionScope } from './ScopeChoice';
import { selectedWithSameShift } from '../scope.Shiftcell';

interface ShiftProps {
    shiftId?: number;
    startTime: string;
    endTime: string;
    color: string;
    isPast: boolean;
    codes: string[];
    passId: string;
    date: Date;
    fullName: string;
    capsuleId: string;
}

const RotatedText = styled.span`
    transform: rotate(-90deg);
    font-weight: bold;
`;
export const ArrowText: React.FC<{}> = ({ }) => {
    return <RotatedText> ⬅ </RotatedText>;
};

export const ShiftTimeDisplay: React.FC<{
    startTime: string;
    endTime: string;
}> = ({ startTime, endTime }) => {
    if (startTime && endTime) {
        return (
            <p>
                {startTime}
                <ArrowText />
                {endTime}
            </p>
        );
    }
    if (startTime && !endTime) {
        return (
            <p>
                {startTime}
                <ArrowText />
            </p>
        );
    }
    return (
        <p>
            <ArrowText />
            {endTime}
        </p>
    );
};
const actionBtnConfig = { width: '18', height: '18', color: 'white' };

const Shift: FC<ShiftProps> = ({
    startTime = '',
    endTime = '',
    color,
    isPast,
    codes,
    capsuleId,
    passId,
    date,
    fullName,
    shiftId,
}: ShiftProps) => {
    const dispatch = useDispatch();
    const tkDispatch = useAppDispatch();

    const { isTargetedToDelete, onDelete } = shiftHooks.useShiftCapsuleDeletionHelpers();

    const isShiftSelected: boolean = useSelector(
        (state: IRootReducer) => state.passengersShiftReudcer.ui.form.isShiftSelected
    );
    const isOpen: boolean = useSelector((state: IRootReducer) => state.passengersShiftReudcer.ui.form.isOpen);
    const isDeletingOrders = useRootAppSelector((state) =>
        apiCallStatusesSelector(state, 'isDeletingOrders')
    );
    const selectedPassengersIds = useRootAppSelector(selectedPassengersIdsSelector);

    const [isHover, setIsHover] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

    const { handleOnMouseEnter, handleOnMouseLeave } = shiftHooks.useMultiDeleteTimer(
        {
            date,
            // codes,
            passId,
            startTime,
            endTime,
            shiftId: shiftId || null,
            times: JSON.stringify([startTime, endTime]),
        },
        openDeleteModal
    );

    const dispatchFormOpenActions = manualOrdersCommonHooks.useClickOpen();

    // -- For whom: this passenger, or every selected passenger with the same shift (customer request, 2026-11-02)
    const capsulesByPassenger = useRootAppSelector(shiftCapsulesByPassengerSelector);
    const [isEditScopeOpen, setIsEditScopeOpen] = useState(false);
    const times = JSON.stringify([startTime, endTime]);
    // Read when a dialog opens, not on every render of every shift in the grid
    const getSameShiftIds = () => selectedWithSameShift({ passId, date, times }, selectedPassengersIds, capsulesByPassenger);
    const [sameShiftCount, setSameShiftCount] = useState(0);

    const onDeleteScopeChange = (scope: ShiftActionScope) => {
        if (scope === 'all') tkDispatch(setCapsulesForDeletionByAllSelectedPassengersAction());
        else
            tkDispatch(
                setShiftTargetedToDeleteAction({ date, passId, startTime, endTime, shiftId: shiftId || null, times })
            );
    };

    const openEdit = (passIds: string[]) => {
        if (!_.isEqual(selectedPassengersIds, passIds)) {
            // dispatch(setAsOnlySelectedPassenger({ passId }));
            tkDispatch(setSelectedPassengersAction(passIds));
        }

        dispatchFormOpenActions();

        dispatch(
            setEditShift({
                date,
                codes,
                passId,
                pickupTime: startTime,
                dropTime: endTime,
                fullName,
            })
        );
        dispatch(onSetSelectedShift(undefined));
    };

    const onClickEdit = () => {
        const sameShiftIds = getSameShiftIds();
        if (sameShiftIds.length > 1) {
            setSameShiftCount(sameShiftIds.length);
            setIsEditScopeOpen(true);
            return;
        }
        openEdit([passId]);
    };

    // The delete window opens on "only this passenger": deleting for all of them is chosen there, in sight,
    // instead of by holding the mouse on the trash icon (that 0.5 s timer no longer acts while it is open)
    const [deleteScope, setDeleteScope] = useState<ShiftActionScope>('one');
    const chooseDeleteScope = (scope: ShiftActionScope) => {
        setDeleteScope(scope);
        onDeleteScopeChange(scope);
    };

    const onClickDelete = () => {
        const count = getSameShiftIds().length;
        setSameShiftCount(count);
        if (count > 1) chooseDeleteScope('one');
        setOpenDeleteModal(true);
    };
    // date === "2025-07-23T21:00:00.000Z" && console.log('  ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^   passId, startTime     ', passId, startTime);

    return (
        <>
            <Slide in appear={false}>
                <Styles.SingleShiftContainer
                    isPast={isPast}
                    formIsOpen={isOpen}
                    isShiftSelected={isShiftSelected}
                    isTargetToDelete={isTargetedToDelete(capsuleId)}
                    // onClick={() => if (date === "2025-07-23T21:00:00.000Z") {console.log(date)}}
                    color={color}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                >
                    {isHover && !isShiftSelected && (
                        <>
                            <Styles.IconContainer
                                style={{ backgroundColor: 'red' }}
                                onClick={onClickDelete}
                                onMouseEnter={handleOnMouseEnter}
                                onMouseLeave={handleOnMouseLeave}
                            >
                                <TrashIcon {...actionBtnConfig} />
                            </Styles.IconContainer>
                            <Styles.IconContainer right onClick={onClickEdit}>
                                <MyEditPencilIcon {...actionBtnConfig} />
                            </Styles.IconContainer>
                        </>
                    )}

                    <ShiftTimeDisplay startTime={startTime} endTime={endTime} />
                </Styles.SingleShiftContainer>
            </Slide>
            <DeleteModal
                isOpen={openDeleteModal}
                onClose={(val) => {
                    setOpenDeleteModal(val);
                }}
                onDelete={() => {
                    onDelete(
                        {
                            codes,
                            passId,
                            date: stringifyDate(date),
                        },
                        () => {
                            setOpenDeleteModal(false)
                            dispatch(setIsShiftDeleted(true));
                        }
                    );
                }}
                startTime={startTime}
                endTime={endTime}
                date={stringifyDate(date)}
                showSpinner={isDeletingOrders === 'LOADING'}
                scope={
                    sameShiftCount > 1
                        ? {
                              value: deleteScope,
                              onChange: chooseDeleteScope,
                              passengerName: fullName,
                              allCount: sameShiftCount,
                          }
                        : undefined
                }
            />
            <EditScopeModal
                isOpen={isEditScopeOpen}
                onClose={() => setIsEditScopeOpen(false)}
                onContinue={(scope) => {
                    setIsEditScopeOpen(false);
                    openEdit(scope === 'all' ? getSameShiftIds() : [passId]);
                }}
                startTime={startTime}
                endTime={endTime}
                date={date}
                passengerName={fullName}
                allCount={sameShiftCount}
            />
        </>
    );
};

export default Shift;
